import { useState } from 'react';
import { cn } from '@/lib/utils';

interface SmartImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /** Texte court affiché en cas d'échec de chargement (ne remplace pas alt) */
  fallbackLabel?: string;
}

/**
 * Image avec :
 *  - Fallback élégant en cas d'échec de chargement (dégradé charte + label).
 *  - Fade-in une fois l'image décodée (évite le "snap" d'apparition brutale).
 *  - decoding="async" pour ne pas bloquer le main thread pendant le décodage.
 *  - Transition combinée opacity + transform : préserve les animations hover
 *    (group-hover:scale-105) appliquées via className parent sans conflit.
 *  - transform-gpu (translateZ(0)) : force le compositing GPU pour fluidifier
 *    les animations hover sur images lourdes.
 */
export const SmartImage = ({
  src,
  alt,
  fallbackLabel,
  className,
  style,
  decoding = 'async',
  ...rest
}: SmartImageProps) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  if (hasError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gradient-to-br from-primary via-[#1a2744] to-accent/50',
          className,
        )}
        role="img"
        aria-label={alt}
      >
        {fallbackLabel && (
          <span className="font-playfair text-white/80 text-sm uppercase tracking-[0.3em] px-4 text-center">
            {fallbackLabel}
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setHasError(true)}
      onLoad={() => setIsLoaded(true)}
      decoding={decoding}
      className={cn(className, 'transform-gpu')}
      style={{
        opacity: isLoaded ? 1 : 0,
        // Transition combinee : opacity pour le fade-in + transform pour
        // les hover:scale appliques par le parent. Override le `transition-*`
        // du className via inline style (priorite CSS).
        transition: 'opacity 400ms ease-out, transform 700ms ease-out',
        ...style,
      }}
      {...rest}
    />
  );
};

export default SmartImage;
